/** GPU Find Edges — fragment-shader port of `lib/filters/find-edges.ts`.
 *
 *  3×3 Sobel on BT.601 luminance, output as inverted grayscale
 *  (flat regions → white, edges → dark traces).
 *
 *  Note on luma weights: this filter uses BT.601 weights (0.299,
 *  0.587, 0.114), NOT Rec. 709 (which our shared color-constants
 *  use for tonal-band weighting in Color Balance and Shadows /
 *  Highlights). The two standards coexist in this codebase by
 *  CPU-side convention — `threshold.ts` and `find-edges.ts` use
 *  BT.601; `color-balance.ts` and `shadows-highlights.ts` use
 *  Rec. 709. Mirroring those choices here keeps GPU/CPU parity
 *  per-filter even when the weights aren't the same across
 *  filters.
 *
 *  The JS path pre-builds a Float32Array of per-pixel luminance to
 *  avoid recomputing in the inner loop; the GPU computes luma
 *  on-the-fly per tap, which is fine because texture sampling is
 *  the dominant cost. */

import { BT601_LUMA_GLSL } from '../glsl-helpers';
import { applyIdentityCopy } from '../identity-copy';
import type { FindEdgesValue } from '../../filters/find-edges';
import { applyShader } from '../runtime';

const FIND_EDGES_FRAG = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_source;
uniform vec2 u_dims;   // (width, height)
uniform float u_k;     // amount / 100
out vec4 outColor;
${BT601_LUMA_GLSL}

// BT.601 luma in the [0, 255] domain — matches the JS path's
// arithmetic (which works on uint8 src values). Weights come from
// the canonical CPU constants via BT601_LUMA_GLSL above.
float luma255(vec3 rgbUnit) {
  return dot(rgbUnit, vec3(BT601_LUMA_R, BT601_LUMA_G, BT601_LUMA_B)) * 255.0;
}

float sampleLuma(float xPx, float yPx) {
  vec2 uv = (vec2(xPx, yPx) + 0.5) / u_dims;
  return luma255(texture(u_source, uv).rgb);
}

void main() {
  vec2 px = v_uv * u_dims - 0.5;
  // Edge-clamped 3×3 neighbor indices. Matches JS exactly:
  // x0 = (x==0 ? 0 : x-1), x2 = (x==maxX ? maxX : x+1).
  float x0 = max(px.x - 1.0, 0.0);
  float x2 = min(px.x + 1.0, u_dims.x - 1.0);
  float y0 = max(px.y - 1.0, 0.0);
  float y2 = min(px.y + 1.0, u_dims.y - 1.0);

  // Sobel kernel taps (center has weight 0, so we skip it):
  //   a b c
  //   d . f
  //   g h i
  float a = sampleLuma(x0, y0);
  float b = sampleLuma(px.x, y0);
  float c = sampleLuma(x2, y0);
  float d = sampleLuma(x0, px.y);
  float f = sampleLuma(x2, px.y);
  float g = sampleLuma(x0, y2);
  float h = sampleLuma(px.x, y2);
  float i = sampleLuma(x2, y2);

  float gx = -a - 2.0 * d - g + c + 2.0 * f + i;
  float gy = -a - 2.0 * b - c + g + 2.0 * h + i;
  float mag = sqrt(gx * gx + gy * gy);

  // Invert + scale: 0 magnitude → white (255), large → dark.
  float e255 = 255.0 - u_k * mag;
  float e = clamp(floor(e255 + 0.5), 0.0, 255.0) / 255.0;

  // Preserve alpha. Grayscale across R/G/B.
  float alpha = texture(u_source, v_uv).a;
  outColor = vec4(e, e, e, alpha);
}
`;

export function applyFindEdgesOnGpu(
  source: ImageData,
  target: HTMLCanvasElement,
  value: FindEdgesValue,
): boolean {
  const amount = Math.max(0, value.amount);
  if (amount === 0) return applyIdentityCopy(source, target);
  return applyShader({
    source,
    target,
    fragShaderSrc: FIND_EDGES_FRAG,
    uniforms: {
      u_dims: { type: 'vec2', value: [source.width, source.height] },
      u_k: { type: 'float', value: amount / 100 },
    },
  });
}
